import { useState, useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

import Steps from "./components/Steps";

import { MODAL_STEPS } from "../../constants/modal";

import { capitalizeFirstLetter, initializeLanguage } from "../../utils";
import "../../i18n";

import CloseIcon from "../../icons/CloseIcon";
import ArrowBackIcon from "../../icons/ArrowBackIcon";
import { Box, IconButton } from "@mui/material";

import { font, titleStyles } from "../../configs";
import { i18n, loadTranslations } from "../../i18n";

import Webcam from "react-webcam";
import { visionInit } from "../../api/endpoints";
import { SpinnerScreen } from "../Spinner";
import { findBrandByDomain } from "../../configs/configLoader";
import {
  countScanAttempt,
  isLinkedFlow,
  trackScanFlow,
} from "../../utils/flowTracking";

import "./index.css";

const Modal = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const camRef = useRef<Webcam | null>(null);

  const [recommendation, setRecommendation] = useState<any | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);

  const targetRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [scanImageUrl, setScanImageUrl] = useState<string | null>(null);
  const [triggerStarted, setTriggerStarted] = useState(false);
  const [top, setTop] = useState(10); // Start at the top

  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  const { t } = useTranslation("components/intro");

  const MODAL_STEPS_TITLE = [
    t("steps.intro"),
    t("steps.intro_info"),
    t("steps.intro_info_foot"),
    t("steps.scan_1"),
    t("steps.scan_2"),
    t("steps.scan_result"),
    t("steps.gender_select"),
    t("steps.size_select"),
    t("steps.loader"),
    t("steps.result"),
    t("steps.error"),
  ];

  const [isTranslationsLoaded, setIsTranslationsLoaded] = useState(false);

  const [step, setStep] = useState<{ number: number }>(MODAL_STEPS.INTRO_INFO);

  const popupRef = useRef<HTMLDivElement | null>(null);
  const photoTakenRef = useRef(false);

  // Journey tracking: the screen on view, when it was entered, and whether the
  // phone was ever held level during this scan.
  const stepNameRef = useRef<string>("");
  const stepEnteredAt = useRef<number | null>(null);
  const scanStartedAt = useRef<number | null>(null);
  const levelReachedRef = useRef(false);
  const cameraAskedAt = useRef<number | null>(null);

  const urlParameters = new URLSearchParams(window.location.search);
  const user_id = urlParameters.get("uid");

  const brandDefined = findBrandByDomain();

  useEffect(() => {
    const handleLocalization = async (lang: string) => {
      try {
        await loadTranslations(lang);
        i18n.changeLanguage(lang);
        setIsTranslationsLoaded(true);
      } catch (error) {
        console.error("Error during localization:", error);
      }
    };

    const lang = initializeLanguage();
    handleLocalization(lang);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStep(MODAL_STEPS.INTRO_INFO);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    countScanAttempt();
    trackScanFlow("webapp_opened", {
      value: isLinkedFlow() ? "linked" : "unlinked",
      reason: user_id ? undefined : "no_uid",
    });

    // Leaving the page (tab switched, app sent to the background, closed) on
    // a screen is where the shopper gave up or went to do something else.
    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        trackScanFlow("page_hidden", { step: stepNameRef.current });
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const name =
      Object.keys(MODAL_STEPS).find(
        (key) =>
          MODAL_STEPS[key as keyof typeof MODAL_STEPS].number === step?.number
      ) ?? "UNKNOWN";
    const now = Date.now();
    trackScanFlow("step_viewed", {
      step: name,
      stepMs:
        stepEnteredAt.current === null
          ? undefined
          : now - stepEnteredAt.current,
    });
    stepNameRef.current = name;
    stepEnteredAt.current = now;

    if (step?.number === MODAL_STEPS.SCAN_1.number) cameraAskedAt.current = now;
    if (step?.number === MODAL_STEPS.SCAN_2.number) {
      scanStartedAt.current = now;
      levelReachedRef.current = false;
    }
  }, [step?.number]);

  // Still not level 15 s into the level screen: the bubble is the friction.
  useEffect(() => {
    if (step?.number !== MODAL_STEPS.SCAN_2.number) return;
    const timer = setTimeout(() => {
      if (!photoTakenRef.current) {
        trackScanFlow("level_slow", {
          step: "SCAN_2",
          value: levelReachedRef.current ? "level_once" : "never_level",
        });
      }
    }, 15000);
    return () => clearTimeout(timer);
  }, [step?.number]);

  const handleGoBack = () => {
    if (step?.number === MODAL_STEPS.INTRO_INFO_FOOT.number) {
      setStep(MODAL_STEPS.INTRO_INFO);
    } else if (step?.number === MODAL_STEPS.GENDER_SELECT.number) {
      setStep(MODAL_STEPS.SCAN_RESULT);
    } else if (step?.number === MODAL_STEPS.SIZE_SELECT.number) {
      const genderFromUrl = urlParameters.get("gender");
      setStep(
        genderFromUrl ? MODAL_STEPS.SCAN_RESULT : MODAL_STEPS.GENDER_SELECT
      );
    }
  };

  const closeIframe = () => {
    trackScanFlow("closed", { step: stepNameRef.current });
    window.parent.postMessage({ action: "closeIframe" }, "*");
  };

  const handleOverlayClick = (e: any) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      closeIframe();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const waitForVideoReady = (): Promise<HTMLVideoElement> => {
    return new Promise((resolve) => {
      const check = () => {
        const video = camRef.current?.video;
        if (
          video &&
          video.readyState >= 3 &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        ) {
          resolve(video);
        } else {
          setTimeout(check, 100);
        }
      };

      check();
    });
  };

  const takePhoto = async () => {
    try {
      const video = await waitForVideoReady();

      if (!camRef.current || !video) return;

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageSrc = canvas.toDataURL("image/jpeg");
        setPhoto(imageSrc);
        photoTakenRef.current = true;
        trackScanFlow("photo_captured", {
          step: "SCAN_2",
          value: scanStartedAt.current
            ? Date.now() - scanStartedAt.current
            : null,
        });
      }
    } catch (err) {
      console.error("Error while taking photo:", err);
      trackScanFlow("photo_capture_failed", {
        step: "SCAN_2",
        reason: (err as Error)?.name || String(err),
      });
    }
  };

  useEffect(() => {
    if (photoTakenRef.current || !isReady) return;

    const handleTakePhoto = async () => {
      await takePhoto();
    };

    const interval = setInterval(() => {
      handleTakePhoto();
      clearInterval(interval);
    }, 800);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const handleUserMedia = async () => {
    if (user_id) {
      const result = await visionInit(user_id);
      if (result?.data?.scan_image_url) {
        setScanImageUrl(result?.data?.scan_image_url);
        trackScanFlow("upload_url_ready");
      } else {
        // Without it the photo cannot be sent: the scan ends on the error.
        trackScanFlow("upload_url_failed", {
          reason: result === false ? "network" : "no_url",
        });
      }
    } else {
      console.log("no user_id found: init failed");
    }
  };

  useEffect(() => {
    if (user_id) {
      handleUserMedia();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  let gammaHistory: number[] = [];

  const handleOrientation = (e: DeviceOrientationEvent) => {
    if (e.beta !== null && e.gamma !== null) {
      // Store last 5 gamma values
      gammaHistory.push(e.gamma);
      if (gammaHistory.length > 5) gammaHistory.shift();

      // Smooth gamma by averaging
      const smoothedGamma =
        gammaHistory.reduce((sum, val) => sum + val, 0) / gammaHistory.length;

      const adjustedBeta = e.beta * Math.cos(smoothedGamma * (Math.PI / 180));

      let newTop = Math.min(90, Math.max(10, 50 - adjustedBeta));
      setTop((prevTop) => prevTop + (newTop - prevTop) * 0.1); // easing

      checkIfReady(newTop);
    }
  };

  const checkIfReady = (newTop: number) => {
    if (targetRef.current && buttonRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const buttonRect = buttonRef.current.getBoundingClientRect();

      const isInside =
        buttonRect.top >= targetRect.top &&
        buttonRect.bottom <= targetRect.bottom &&
        buttonRect.left >= targetRect.left &&
        buttonRect.right <= targetRect.right;

      if (isInside) {
        console.log("✅ Inside target – isReady = true");
        if (!levelReachedRef.current && scanStartedAt.current) {
          levelReachedRef.current = true;
          trackScanFlow("level_reached", {
            step: "SCAN_2",
            value: Date.now() - scanStartedAt.current,
          });
        }
        setIsReady(true);
      } else {
        console.log("❌ Outside target – isReady = false");
        setIsReady(false);
      }
    }
  };

  const handleButtonsForScan = () => {
    return (
      <>
        <Box
          ref={targetRef}
          sx={{
            width: 144,
            height: 144,
            backgroundColor: "transparent",
            position: "absolute",
            top: "345px",
            left: "50%",
            transform: "translateX(-50%)",
            borderRadius: "50%",
            border: isReady
              ? "9px solid rgba(46, 165, 36, 0.3)"
              : "9px solid rgba(0, 0, 0, 0.3)",
            zIndex: "1 !important",
          }}
        />
        <Box
          ref={buttonRef}
          sx={{
            width: 132,
            height: 132,
            backgroundColor: isReady ? "rgba(46, 165, 36, 0.9)" : "white",
            position: "absolute",
            top: `${top}%`,
            transition: "top 0.1s ease-out",
            transform: "translateX(-50%)",
            borderRadius: "50%",
            cursor: "grab",
            zIndex: "1 !important",
          }}
        />
      </>
    );
  };

  const restart = () => {
    countScanAttempt();
    setPhoto(null);
    setTriggerStarted(false);
    setIsReady(false);
    setStep(MODAL_STEPS.INTRO);
    setRecommendation(null);
    setScanImageUrl(null);
    setTop(10);

    if (user_id) {
      handleUserMedia();
    }
  };

  const headerStyles = {
    marginTop:
      isMobile && brandDefined?.name === "SRP"
        ? 0
        : isMobile && brandDefined?.name !== "SRP"
          ? "10px"
          : 0,
    marginBottom:
      step?.number === MODAL_STEPS.SCAN_1.number ||
      step?.number === MODAL_STEPS.SCAN_2.number
        ? 0
        : "10px",
    width: brandDefined?.name === "SRP" ? "calc(100% + 50px)" : "100%",
    position: (brandDefined?.name === "SRP"
      ? "relative"
      : "initial") as React.CSSProperties["position"],
    right: brandDefined?.name === "SRP" ? "25px" : "0",
    bottom: brandDefined?.name === "SRP" ? "20px" : "0",
    backgroundColor: brandDefined?.name === "SRP" ? "#FFFFFF" : "inherit",
    minHeight: brandDefined?.name === "SRP" ? "45px" : "auto",
    borderRadius: !isMobile && brandDefined?.name === "SRP" ? "8px" : 0,
  };

  const titleText =
    step?.number === MODAL_STEPS.LOADER.number
      ? t("steps.loader")
      : MODAL_STEPS_TITLE[step?.number];

  return (
    <Box
      className="overlay"
      onClick={(e: any) => {
        const target = e.target as HTMLElement;

        if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
          return;
        }

        if (target.classList.contains("overlay")) {
          handleOverlayClick(e);
        }
      }}
      style={{
        fontFamily: isMobile
          ? `${font}, sans-serif`
          : `${font}, sans-serif !important`,
      }}
    >
      {step?.number === MODAL_STEPS.SCAN_1.number ||
      step?.number === MODAL_STEPS.SCAN_2.number ? (
        <>
          {showSpinner && <SpinnerScreen />}
          <Box>
            {step?.number === MODAL_STEPS.SCAN_2.number
              ? handleButtonsForScan()
              : null}
            <Webcam
              ref={camRef}
              mirrored={false}
              height={screenHeight}
              width={screenWidth}
              screenshotFormat="image/jpeg"
              screenshotQuality={1}
              style={{ position: "absolute", top: 0, left: 0 }}
              videoConstraints={{
                width: { min: 1280, ideal: 1920 },
                height: { min: 720, ideal: 1080 },
                facingMode: "environment",
                // facingMode: "user", // change to track front camera
                aspectRatio: screenHeight / screenWidth,
              }}
              onUserMedia={() => {
                trackScanFlow("camera_ready", {
                  step: stepNameRef.current,
                  value: cameraAskedAt.current
                    ? Date.now() - cameraAskedAt.current
                    : null,
                });
              }}
              onUserMediaError={(e) => {
                console.log("UserMediaError:", e);
                // Nothing is shown: the shopper is left on a black screen.
                trackScanFlow("camera_error", {
                  step: stepNameRef.current,
                  reason:
                    typeof e === "string" ? e : (e as Error)?.name || "unknown",
                });
              }}
            />
          </Box>
        </>
      ) : null}
      <Box
        className="popup"
        ref={popupRef}
        onClick={(e: any) => e.stopPropagation()}
        style={{
          fontFamily: isMobile
            ? `${font}, sans-serif`
            : `${font}, sans-serif !important`,
          backgroundColor: brandDefined?.name === "SRP" ? "#F7F7F7" : "#FFFFFF",
          paddingTop: isMobile && brandDefined?.name === "SRP" ? 0 : "20px",
          height:
            isMobile && brandDefined?.name === "SRP"
              ? "calc(100% - 80px)"
              : "auto",
        }}
      >
        <Box>
          {isTranslationsLoaded ? (
            <>
              <Box className="popup-header" style={headerStyles}>
                <div
                  className="left-arrow"
                  style={{
                    position: "absolute",
                    left: "15px",
                    top: "5px",
                  }}
                >
                  {step?.number >= 0 &&
                    step?.number !== MODAL_STEPS.INTRO_INFO.number &&
                    step?.number !== MODAL_STEPS.SCAN_1.number &&
                    step?.number !== MODAL_STEPS.SCAN_2.number &&
                    step?.number !== MODAL_STEPS.SCAN_RESULT.number &&
                    step?.number !== MODAL_STEPS.ERROR.number &&
                    step?.number !== MODAL_STEPS.LOADER.number &&
                    step?.number !== MODAL_STEPS.RESULT.number && (
                      <IconButton
                        sx={{
                          zIndex: 1,
                          width: "31px",
                          height: "31px",
                          padding: "3px",
                          borderRadius: "5px",
                        }}
                        onClick={handleGoBack}
                      >
                        <ArrowBackIcon
                          style={{
                            color: "black",
                            width: isMobile ? "16.5px" : "13.5px",
                            height: isMobile ? "14px" : "11px",
                          }}
                        />
                      </IconButton>
                    )}
                </div>
                {step?.number >= 0 &&
                step?.number !== MODAL_STEPS.SCAN_1.number &&
                step?.number !== MODAL_STEPS.SCAN_2.number ? (
                  <IconButton
                    onClick={() => closeIframe()}
                    className="close-icon"
                    sx={{
                      width: "45px",
                      height: "45px",
                      top: "5px",
                    }}
                  >
                    <CloseIcon
                      className="icon"
                      style={{
                        color: "black",
                        width: isMobile ? "14px" : "11px",
                        height: isMobile ? "14px" : "11px",
                      }}
                    />
                  </IconButton>
                ) : null}
              </Box>
              <Box
                className="popup-content"
                style={{
                  backgroundColor:
                    brandDefined?.name === "SRP" ? "#F7F7F7" : "#FFFFFF",
                }}
              >
                {isMobile &&
                  step?.number !== MODAL_STEPS.SCAN_1.number &&
                  step?.number !== MODAL_STEPS.SCAN_2.number &&
                  step?.number !== MODAL_STEPS.SCAN_RESULT.number && (
                    <h2
                      style={{
                        textAlign:
                          titleStyles.textAlign as React.CSSProperties["textAlign"],
                        textTransform:
                          titleStyles.textTransform === "capitalize"
                            ? "none"
                            : titleStyles.textTransform,
                        color: "#2E2E2E",
                        fontSize: "18px",
                        fontWeight: 700,
                        minHeight: "25px",
                        marginTop: "25px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          titleStyles.textTransform === "capitalize"
                            ? capitalizeFirstLetter(t(titleText))
                            : t(titleText),
                      }}
                    />
                  )}
                <Steps
                  step={step}
                  setStep={setStep}
                  photo={photo}
                  scanImageUrl={scanImageUrl}
                  recommendation={recommendation}
                  triggerStarted={triggerStarted}
                  setTriggerStarted={setTriggerStarted}
                  handleOrientation={handleOrientation}
                  restart={restart}
                  setShowSpinner={setShowSpinner}
                  isReady={isReady}
                />
              </Box>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default Modal;
